export type MiniproFeatures = {
  home_stats: {
    enabled: boolean;
    virtual_views: number;
    virtual_entries: number;
    virtual_dials: number;
  };
  float_actions: {
    enabled: boolean;
    publish_qr_path: string;
    promo_qr_path: string;
  };
  home_ads: {
    enabled: boolean;
    /** 无有效广告时占位条展示文案 */
    placeholder_title: string;
    /** 无有效广告时占位条点击弹出的客服微信二维码 */
    placeholder_qr_path: string;
  };
  dial_bubble: {
    enabled: boolean;
    /** 支持 {count}、{dial_count}、{拨号量} */
    text_template: string;
  };
  auto_dial: { enabled: boolean; tier: 1 | 2 | 3 };
  publisher_ads: {
    enabled: boolean;
    custom_unit_id: string;
    interstitial_unit_id: string;
    rewarded_unit_id: string;
    custom_enabled: boolean;
    /** 首页列表信息流 */
    custom_index_feed_enabled: boolean;
    /** 详情页正文下方 */
    custom_detail_main_enabled: boolean;
    /** 详情页「更多推荐」列表 */
    custom_detail_more_enabled: boolean;
    interstitial_enabled: boolean;
    rewarded_enabled: boolean;
    /** 用户关闭插屏后弹出激励引导（须用户点「去观看」才播视频） */
    rewarded_prompt_after_interstitial: boolean;
    feed_interval: number;
    daily_open_cap: number;
    total_open_cap: number;
    rewarded_skip_label: string;
    rewarded_prompt_title: string;
    rewarded_prompt_confirm_text: string;
    rewarded_prompt_cancel_text: string;
  };
};

export function defaultMiniproFeatures(): MiniproFeatures {
  return {
    home_stats: { enabled: false, virtual_views: 0, virtual_entries: 0, virtual_dials: 0 },
    float_actions: { enabled: false, publish_qr_path: '', promo_qr_path: '' },
    home_ads: { enabled: false, placeholder_title: '虚位以待', placeholder_qr_path: '' },
    dial_bubble: { enabled: false, text_template: '已有 {count} 人联系' },
    auto_dial: { enabled: false, tier: 2 },
    publisher_ads: {
      enabled: false,
      custom_unit_id: '',
      interstitial_unit_id: '',
      rewarded_unit_id: '',
      custom_enabled: true,
      custom_index_feed_enabled: false,
      custom_detail_main_enabled: true,
      custom_detail_more_enabled: false,
      interstitial_enabled: true,
      rewarded_enabled: true,
      rewarded_prompt_after_interstitial: true,
      feed_interval: 5,
      daily_open_cap: 3,
      total_open_cap: 50,
      rewarded_skip_label: '看视频，今天不弹窗',
      rewarded_prompt_title: '刚关闭的是广告弹窗',
      rewarded_prompt_confirm_text: '去观看',
      rewarded_prompt_cancel_text: '暂不需要',
    },
  };
}

export type NormalizeMiniproFeaturesOpts = {
  /** 管理后台编辑：保留未填完的广告草稿行 */
  forEdit?: boolean;
};

export function normalizeMiniproFeatures(
  raw: unknown,
  opts?: NormalizeMiniproFeaturesOpts,
): MiniproFeatures {
  const d = defaultMiniproFeatures();
  if (!raw || typeof raw !== 'object') return d;
  const o = raw as Record<string, unknown>;
  const hs = o.home_stats && typeof o.home_stats === 'object' ? (o.home_stats as Record<string, unknown>) : {};
  const fa = o.float_actions && typeof o.float_actions === 'object' ? (o.float_actions as Record<string, unknown>) : {};
  const ha = o.home_ads && typeof o.home_ads === 'object' ? (o.home_ads as Record<string, unknown>) : {};
  const db = o.dial_bubble && typeof o.dial_bubble === 'object' ? (o.dial_bubble as Record<string, unknown>) : {};
  const ad = o.auto_dial && typeof o.auto_dial === 'object' ? (o.auto_dial as Record<string, unknown>) : {};
  const pa = o.publisher_ads && typeof o.publisher_ads === 'object' ? (o.publisher_ads as Record<string, unknown>) : {};
  let tier = Number(ad.tier) || 2;
  if (tier < 1) tier = 1;
  if (tier > 3) tier = 3;
  const customEnabled = pa.custom_enabled !== false;
  const indexFeedEnabled =
    pa.custom_index_feed_enabled !== undefined ? !!pa.custom_index_feed_enabled : customEnabled;
  const detailMainEnabled =
    pa.custom_detail_main_enabled !== undefined ? !!pa.custom_detail_main_enabled : customEnabled;
  const detailMoreEnabled =
    pa.custom_detail_more_enabled !== undefined ? !!pa.custom_detail_more_enabled : false;
  return {
    home_stats: {
      enabled: !!hs.enabled,
      virtual_views: Math.max(0, Number(hs.virtual_views) || 0),
      virtual_entries: Math.max(0, Number(hs.virtual_entries) || 0),
      virtual_dials: Math.max(0, Number(hs.virtual_dials) || 0),
    },
    float_actions: {
      enabled: !!fa.enabled,
      publish_qr_path: String(fa.publish_qr_path || '').trim(),
      promo_qr_path: String(fa.promo_qr_path || '').trim(),
    },
    home_ads: {
      enabled: !!ha.enabled,
      placeholder_title: String(ha.placeholder_title || '虚位以待').trim() || '虚位以待',
      placeholder_qr_path: String(ha.placeholder_qr_path || '').trim(),
    },
    dial_bubble: {
      enabled: !!db.enabled,
      text_template: String(db.text_template || '已有 {count} 人联系').trim() || '已有 {count} 人联系',
    },
    auto_dial: { enabled: !!ad.enabled, tier: tier as 1 | 2 | 3 },
    publisher_ads: {
      enabled: !!pa.enabled,
      custom_unit_id: String(pa.custom_unit_id || '').trim(),
      interstitial_unit_id: String(pa.interstitial_unit_id || '').trim(),
      rewarded_unit_id: String(pa.rewarded_unit_id || '').trim(),
      custom_enabled: customEnabled,
      custom_index_feed_enabled: indexFeedEnabled,
      custom_detail_main_enabled: detailMainEnabled,
      custom_detail_more_enabled: detailMoreEnabled,
      interstitial_enabled: pa.interstitial_enabled !== false,
      rewarded_enabled: pa.rewarded_enabled !== false,
      rewarded_prompt_after_interstitial: pa.rewarded_prompt_after_interstitial !== false,
      feed_interval: Math.min(20, Math.max(3, Number(pa.feed_interval) || 5)),
      daily_open_cap: Math.min(20, Math.max(1, Number(pa.daily_open_cap) || 3)),
      total_open_cap: Math.min(9999, Math.max(1, Number(pa.total_open_cap) || 50)),
      rewarded_skip_label:
        String(pa.rewarded_skip_label || '看视频，今天不弹窗').trim() || '看视频，今天不弹窗',
      rewarded_prompt_title:
        String(pa.rewarded_prompt_title || '刚关闭的是广告弹窗').trim() || '刚关闭的是广告弹窗',
      rewarded_prompt_confirm_text:
        String(pa.rewarded_prompt_confirm_text || '去观看').trim() || '去观看',
      rewarded_prompt_cancel_text:
        String(pa.rewarded_prompt_cancel_text || '暂不需要').trim() || '暂不需要',
    },
  };
}
